import React, { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { CSSTransition } from 'react-transition-group';

import haircareSteps from './lib/haircareQuiz';
import supplementSteps from './lib/supplementQuiz';
import submitQuiz from './lib/api';
import subscribeMailchimp from './lib/subscribeMailchimp';
import {quizConditionsOnNext, quizConditionsOnBack} from "./lib/processQuizConditions";

import Navigation from './components/Navigation';
import { Screen } from './components/Screens';
import Footer from './components/Footer';
import ErrorMessage from './components/ErrorMessage';
import ConfirmDialog from './components/ConfirmDialog';
import QuizHistory from './components/QuizHistory';
import { normaliseKey } from './lib/string';

const defaultState = {
  activeStep: 0,
  activeSubStep: 0,
  highestStep: 0,
  submitting: false,
  hideScreen: false,
  persistedQuery: null,
};

const App = () => {
  const quizType = window.location.pathname.split('/')[1].split('-').pop();
  window.localStorage.setItem('quiz-type', quizType);

  const [hasLoadedState, setHasLoadedState] = useState(false);
  const [state, setState] = useState(defaultState);
  const [apiError, setApiError] = useState(null);
  const [hideQuizHistory, setHideQuizHistory] = useState(true);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const methods = useForm({ mode: 'all' });
  let steps = (quizType === 'supplement') ? supplementSteps : haircareSteps;

  // Load persisted state on mount.
  useEffect(() => {
    try {
      let persisted = {};

      // Load state from hash data.
      if (window.quizData && window.quizData.data) {
        const hashData = JSON.parse(window.quizData.data);

        Object.keys(hashData).forEach((key) => {
          persisted[normaliseKey(key)] = hashData[key];
        });

        if (window.quizData.hash) {
          persisted.previousHash = window.quizData.hash;
        }

        if (window.quizData.persistedQuery) {
          persisted.persistedQuery = window.quizData.persistedQuery;
        }

        persisted.highestStep = steps.length - 1;
      } else {
        persisted = JSON.parse(localStorage.getItem('order-form-state-' + quizType));
      }

      const { hash } = window.location;
      if (persisted !== null && hash.substring(1)) {
        const parts = hash.substring(1).split('.');
        const activeStep = parseInt(parts[0], 10) || 0;
        const activeSubStep = parseInt(parts[1], 10) || 0;

        if (activeStep <= persisted.highestStep) {
          persisted.activeStep = activeStep;
          persisted.activeSubStep = activeSubStep;
        }
      }

      toggleQuizHistory();

      // Merge default state with persisted state to include new properties.
      setState({ ...defaultState, ...persisted });
    } catch (error) {
      //
    }

    setHasLoadedState(true);
  }, []);

  // Persist state.
  useEffect(() => {
    try {
      const { submitting, ...rest } = state;
      localStorage.setItem('order-form-state-' + quizType, JSON.stringify(rest));
    } catch (error) {
      //
    }

    window.onpopstate = (e) => {
      const { activeStep = 0, activeSubStep = 0 } = e.state || {};
      setState({ ...state, activeStep, activeSubStep });
    };
  }, [state]);

  // Scroll to top on step change.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [state.activeStep, state.activeSubStep]);

  /**
   * On step next.
   * @param {object} data
   */
  const onNext = async (data) => {
    setHideQuizHistory(true);

    if (state.submitting) {
      return;
    }

    // catch up with quiz data after page reload
    data = {
      ...state,
      ...data
    }

    setState({ ...state, submitting: true });

    // Imitate loading state.
    await new Promise((resolve) => {
      setTimeout(async () => {
        let { activeStep, activeSubStep, highestStep } = state;
        const subSteps = steps[activeStep].steps.length;
        const originalStep = activeStep;
        const originalSubStep = activeSubStep;

        if (activeSubStep < subSteps - 1) {
          // There's another substep available.
          activeSubStep += 1;
        } else if (activeStep === steps.length - 1) {
          setApiError(null);

          // Quiz is complete, submit to the API.
          await handleCompleted(data);

          return;
        } else {
          // No more substeps, continue to next screen.
          activeStep += 1;
          activeSubStep = 0;
        }

        // Handle steps conditional logic
        let processedSteps = await quizConditionsOnNext(
          activeStep,
          activeSubStep,
          steps,
          data
        );

        ({ activeStep, activeSubStep } = processedSteps);

        highestStep = Math.max(activeStep, highestStep);

        const finalState = {
          ...state,
          ...data,
          activeStep,
          activeSubStep,
          highestStep,
          submitting: false,
          hideScreen: true,
        };

        setState(finalState);

        setTimeout(() => {
          setState((newState) => {
            // Fire the onNext call to mutate state.
            const step = steps[originalStep].steps[originalSubStep];
            if (step && step.onNext) {
              newState = step.onNext(newState, methods.setValue);
            }

            return { ...newState, hideScreen: false };
          });

          window.history.pushState({ activeStep, activeSubStep }, 'Quiz', window.location.pathname);

          resolve();
        }, 10);
      }, 190);
    });
  };

  const handleCompleted = async (data) => {
    const quizData = { ...state, ...data };
    const { activeStep: _, activeSubStep: _2, highestStep: _3, submitting, hideScreen, ...filteredData } = quizData;

    //subscribe mailchimp
    console.log('localStorage:',localStorage.getItem('quiz-type'));
    if(data.yourEmailAddress !== ''){
      const successSubscribe = await subscribeMailchimp(filteredData);
      if (!successSubscribe) {
        setApiError('There was a problem subscribing email address. Please contact us.');
      }
    }

    // submit data
    const success = await submitQuiz(filteredData);
    if (!success) {
      setApiError('There was a problem retrieving the ingredients list. Please contact us.');
    }
  }


  /**
   * On step back clicked.
   */
  const onBack = async () => {
    let { activeStep, activeSubStep } = state;
    const quizType = window.localStorage.getItem('quiz-type');
    const data = JSON.parse(localStorage.getItem('order-form-state-' + quizType));

    if (activeSubStep > 0) {
      activeSubStep -= 1;
    } else {
      activeStep -= 1;
      activeSubStep = steps[activeStep].steps.length - 1;
    }

    const nextStepHeading = steps[activeStep].steps[activeSubStep].heading;
    let processedSteps = await quizConditionsOnBack(
      activeStep,
      activeSubStep,
      nextStepHeading,
      data
    );

    ({ activeStep, activeSubStep } = processedSteps);

    setState({ ...state, activeStep, activeSubStep });
  };

  const toggleQuizHistory = () => {
    const quizHistory = localStorage.getItem('quiz-history-' + quizType);
    const quizFormState = JSON.parse(localStorage.getItem('order-form-state-' + quizType));

    // get query string param
    const urlParams = new URLSearchParams(window.location.search);
    const quizHash = urlParams.get('q');

    if (quizHistory && !quizHash) {
      if (!quizFormState) {
        setHideQuizHistory(false);
      } else if (quizFormState.activeStep === 0 && quizFormState.activeSubStep === 0 && quizFormState.highestStep === 0) {
        setHideQuizHistory(false);
      }
    }

  };

  /**
   * Wipe all current quiz data and reload the page
   */
  const resetQuiz = async () => {

    // reset everything
    setState({ ...defaultState, submitting: true });
    methods.reset();

    // slight timeout or it looks a bit jumpy
    setTimeout(() => {
      // manually redirect to the quiz page in case we have a hash in the url
      window.location.href = '/quiz-' + quizType + '/';
    }, 250)

  };

  /**
   * On save and exit.
   */
  const onSave = async () => {
    const formData = methods.getValues();

    if (!state.previousHash) {
      setState(prevState => ({ ...prevState, ...formData, submitting: true }));
      window.location = '/';
      return;
    }

    const { whatCombinationOfProductsDoYouWantToBuy } = formData;
    let newState = { ...state };
    const { products } = window.quizData;
    let smellAndColor = {};

    const setSmellAndColor = (sourceColor, sourceSmell, targetColor, targetSmell) => {
      if (state[sourceColor] && state[sourceSmell]) {
        smellAndColor[targetColor] = state[sourceColor];
        smellAndColor[targetSmell] = state[sourceSmell];

        delete newState[sourceColor];
        delete newState[sourceSmell];
      }
    };

    switch (whatCombinationOfProductsDoYouWantToBuy) {
      case products.SHAMPOO.label:
        setSmellAndColor(
          'whatColourDoYouWantYourConditionerToBe',
          'whatDoYouWantYourConditionerToSmellLike',
          'whatColourDoYouWantYourShampooToBe',
          'whatDoYouWantYourShampooToSmellLike'
        );
        break;
      case products.CONDITIONER.label:
        setSmellAndColor(
          'whatColourDoYouWantYourShampooToBe',
          'whatDoYouWantYourShampooToSmellLike',
          'whatColourDoYouWantYourConditionerToBe',
          'whatDoYouWantYourConditionerToSmellLike'
        );
        break;
      case products.SHAMPOO_AND_CONDITIONER.label:
        if (state.whatColourDoYouWantYourConditionerToBe && state.whatDoYouWantYourConditionerToSmellLike) {
          smellAndColor.whatColourDoYouWantYourShampooToBe = state.whatColourDoYouWantYourConditionerToBe;
          smellAndColor.whatDoYouWantYourShampooToSmellLike = state.whatDoYouWantYourConditionerToSmellLike;
        }
        if (state.whatColourDoYouWantYourShampooToBe && state.whatDoYouWantYourShampooToSmellLike) {
          smellAndColor.whatColourDoYouWantYourConditionerToBe = state.whatColourDoYouWantYourShampooToBe;
          smellAndColor.whatDoYouWantYourConditionerToSmellLike = state.whatDoYouWantYourShampooToSmellLike;
        }
        break;
      default:
        break;
    }

    newState = { ...newState, ...smellAndColor };

    const quizData = { ...newState, ...formData };
    const { activeStep, activeSubStep, highestStep, submitting, hideScreen, ...filteredData } = quizData;

    await submitQuiz(filteredData);
  };

  /**
   * Render a step.
   */
  const renderStep = () => {
    const step = steps[state.activeStep];
    const subStep = step.steps[state.activeSubStep];

    let { screen: ScreenComponent = null } = subStep;

    if (!ScreenComponent) {
      ScreenComponent = Screen;
    }

    return (
      <>
        {state.submitting && <div className="quiz-loading-block" />}
        <CSSTransition in={!state.submitting} timeout={200} classNames="step-transition">
          <ScreenComponent key={subStep.heading} state={state} step={subStep} onNext={onNext} />
        </CSSTransition>
      </>
    );
  };

  return (
    <>

      {!hideQuizHistory && (
        <QuizHistory onClose={() => setHideQuizHistory(true)} />
      )}

      <Navigation
        prevHash={state.previousHash}
        steps={steps}
        activeStep={state.activeStep}
        highestStep={state.highestStep}
        onSave={onSave}
        onNavigation={(step) => {
          if (step <= state.highestStep) {
            setState({ ...state, activeStep: step, activeSubStep: 0 });
            window.history.pushState({ activeStep: step, activeSubStep: 0 }, 'Quiz', window.location.pathname);
          }
        }}
      />

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onNext)} className="position-relative">
          {hasLoadedState && renderStep()}

          {apiError !== null && (
            <ErrorMessage message={apiError} isModal onClose={() => setApiError(null)} />
          )}

          {confirmMessage !== null && (
            <ConfirmDialog
              message={confirmMessage}
              onConfirm={() => resetQuiz()}
              onClose={() => setConfirmMessage(null)}
              submitting={state.submitting}
            />
          )}

          <Footer
            prevHash={state.previousHash}
            canBack={state.activeStep > 0 || state.activeSubStep > 0}
            canNext={methods.formState.isValid}
            onSave={onSave}
            onBack={onBack}
            onReset={(message) => {
              setConfirmMessage(message);
            }}
            submitting={state.submitting}
          />
        </form>
      </FormProvider>
    </>
  );
};

export default App;
